import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, Table, Badge, Form, Button } from 'react-bootstrap';
import { UsersIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { companyAPI } from '../../services/api';

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  true:  { bg: '#dcfce7', color: '#166534' },
  false: { bg: '#fee2e2', color: '#991b1b' },
};

export default function WebstoreCustomers() {
  const [members, setMembers] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await companyAPI.getRetailMembers();
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (member: any) => {
    try {
      await companyAPI.updateRetailMember(member.id, { is_active: !member.is_active });
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, is_active: !m.is_active } : m));
    } catch {}
  };

  const filtered = members.filter(m =>
    `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 style={{ fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>
              <UsersIcon style={{ width: 22, height: 22, marginRight: 8, color: '#059669', verticalAlign: 'middle' }} />
              Store Customers
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', margin: 0 }}>
              Members registered on your retail store — {members.length} total
            </p>
          </div>
          <div style={{ position: 'relative', width: '240px' }}>
            <MagnifyingGlassIcon style={{ width: 16, height: 16, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <Form.Control size="sm" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: '32px' }} />
          </div>
        </div>

        <Card className="border-0 shadow-sm" style={{ borderRadius: '14px' }}>
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-success" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-5" style={{ color: '#94a3b8' }}>
              <UsersIcon style={{ width: 40, height: 40, marginBottom: 12, color: '#e2e8f0' }} />
              <p style={{ fontWeight: 500 }}>{search ? 'No matching customers' : 'No store customers yet'}</p>
              <p style={{ fontSize: '0.82rem' }}>Customers who register on your /store page will appear here.</p>
            </div>
          ) : (
            <Table className="mb-0" style={{ fontSize: '0.88rem' }}>
              <thead style={{ background: '#f8fafc' }}>
                <tr>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Name</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Email</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Phone</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Joined</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}>Status</th>
                  <th style={{ padding: '12px 20px', fontWeight: 600, color: '#475569' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 20px', fontWeight: 600, color: '#1e293b' }}>{m.full_name}</td>
                    <td style={{ padding: '12px 20px', color: '#64748b' }}>{m.email}</td>
                    <td style={{ padding: '12px 20px', color: '#64748b' }}>{m.phone || '—'}</td>
                    <td style={{ padding: '12px 20px', color: '#94a3b8', fontSize: '0.8rem' }}>
                      {new Date(m.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      <Badge bg="" style={{ ...STATUS_COLORS[String(m.is_active)], fontSize: '0.75rem', borderRadius: '12px' }}>
                        {m.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      <Button variant="link" size="sm" className="p-0" style={{ color: '#059669', fontSize: '0.8rem' }} onClick={() => toggleActive(m)}>
                        {m.is_active ? 'Disable' : 'Enable'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </Container>
    </CompanyLayout>
  );
}
